import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import client from '../api/client';

const ChatContext = createContext(null);

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_API_URL || '').replace(/\/api\/v1\/?$/, '') ||
  '';

export function ChatProvider({ user, children }) {
  const [contacts, setContacts]   = useState([]);
  const [messages, setMessages]   = useState({});   // { [contactUserId]: Message[] }
  const [activeContact, setActiveContact] = useState(null);
  const [typing, setTyping]       = useState({});   // { [userId]: bool }
  const [connected, setConnected] = useState(false);
  const [sendError, setSendError] = useState(null);
  const socketRef      = useRef(null);
  const typingTimers   = useRef({});
  // Tracks which contacts have had their full history fetched at least once
  const historyLoaded  = useRef(new Set());
  // Live ref to activeContact so socket handlers can read it without stale closure
  const activeContactRef = useRef(null);

  // Keep a live ref to user so handlers always read the latest value
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // Keep activeContactRef in sync so socket handlers can check it without stale closure
  useEffect(() => { activeContactRef.current = activeContact; }, [activeContact]);

  // Stable identity — only recreate the socket when userId or role changes,
  // not on every patchUser() call (check-in, profile edits, etc.)
  const userId   = (user?._id || user?.id)?.toString();
  const userRole = user?.role;

  // Profile _id: Employee._id for teachers/coordinators, Student._id for students.
  // This matches what the backend uses as senderId/receiverId in messages.
  const myProfileId = (['teacher', 'coordinator'].includes(userRole)
    ? (user?.teacherDocId || user?.employeeId)
    : user?.studentId
  )?.toString() || userId;

  // ── Socket ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !['student', 'teacher', 'coordinator'].includes(userRole)) return;
    const token = localStorage.getItem('edupulse_token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('chat:message', (msg) => {
      // Find the contact this message belongs to by matching either senderId
      // or receiverId against the contacts list. This avoids any dependency on
      // computing "myId" (which requires knowing the profile _id).
      setContacts(prevContacts => {
        const contact = prevContacts.find(
          c => c.userId === msg.senderId || c.userId === msg.receiverId
        );
        const contactId = contact?.userId;
        const isIncoming = contact && contact.userId === msg.senderId;

        if (contactId) {
          setMessages(prevMessages => ({
            ...prevMessages,
            [contactId]: [...(prevMessages[contactId] || []), msg],
          }));
        }

        // Browser notification for incoming messages when contact is not active
        if (isIncoming && contact) {
          const activeId = activeContactRef.current?.userId;
          if (activeId !== contact.userId && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
              const preview = msg.message.length > 80 ? msg.message.slice(0, 77) + '…' : msg.message;
              new Notification(contact.name, { body: preview, icon: '/logo192.png' });
            } catch {
              // Notification API unavailable (e.g. iOS PWA) — push subscription handles delivery
            }
          }
        }

        // Bump unread only for incoming messages
        if (isIncoming) {
          return prevContacts.map(c =>
            c.userId === msg.senderId ? { ...c, unread: (c.unread || 0) + 1 } : c
          );
        }
        return prevContacts;
      });
    });

    socket.on('chat:typing', ({ fromUserId }) => {
      setTyping(prev => ({ ...prev, [fromUserId]: true }));
      clearTimeout(typingTimers.current[fromUserId]);
      typingTimers.current[fromUserId] = setTimeout(
        () => setTyping(prev => ({ ...prev, [fromUserId]: false })),
        2500
      );
    });

    socket.on('chat:error', (err) => {
      console.error('[chat]', err.message);
      setSendError(err.message || 'Failed to send message');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userRole]);

  // ── REST helpers ──────────────────────────────────────────────────────────
  const loadContacts = useCallback(async () => {
    try {
      const { data } = await client.get('/chat/contacts');
      setContacts(data.contacts || []);
    } catch (err) {
      console.error('[chat] loadContacts:', err.message);
    }
  }, []);

  const loadHistory = useCallback(async (contactUserId) => {
    try {
      const { data } = await client.get(`/chat/history/${contactUserId}`);
      const fetched = data.messages || [];
      setMessages(prev => {
        // Merge fetched history with any real-time messages already in state
        const existing = prev[contactUserId] || [];
        const seen = new Set(fetched.map(m => m._id));
        const extra = existing.filter(m => !seen.has(m._id));
        const merged = [...fetched, ...extra].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        return { ...prev, [contactUserId]: merged };
      });
      setContacts(prev =>
        prev.map(c => c.userId === contactUserId ? { ...c, unread: 0 } : c)
      );
      client.patch(`/chat/read/${contactUserId}`).catch(() => {});
    } catch (err) {
      console.error('[chat] loadHistory:', err.message);
    }
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────
  const sendMessage = useCallback((toUserId, message) => {
    if (!socketRef.current?.connected || !message.trim()) {
      setSendError(!socketRef.current?.connected ? 'Not connected — check your internet' : null);
      return false;
    }
    setSendError(null);
    socketRef.current.emit('chat:send', { toUserId, message: message.trim() });
    return true;
  }, []);

  const sendTyping = useCallback((toUserId) => {
    socketRef.current?.emit('chat:typing', { toUserId });
  }, []);

  const openContact = useCallback(async (contact) => {
    setActiveContact(contact);
    // Always load history the FIRST time a contact is opened, regardless of
    // whether real-time messages already exist in state for that contact.
    if (!historyLoaded.current.has(contact.userId)) {
      historyLoaded.current.add(contact.userId);
      await loadHistory(contact.userId);
    } else {
      setContacts(prev =>
        prev.map(c => c.userId === contact.userId ? { ...c, unread: 0 } : c)
      );
      client.patch(`/chat/read/${contact.userId}`).catch(() => {});
    }
  }, [loadHistory]);

  // Load contacts once on mount (or when user identity changes)
  useEffect(() => {
    if (userId && ['student', 'teacher', 'coordinator'].includes(userRole)) {
      historyLoaded.current.clear();
      loadContacts();
    }
  }, [userId, userRole, loadContacts]);

  const totalUnread = contacts.reduce((sum, c) => sum + (c.unread || 0), 0);

  return (
    <ChatContext.Provider value={{
      connected, contacts, messages, activeContact, typing,
      sendError, setSendError,
      openContact, sendMessage, sendTyping, loadContacts,
      totalUnread, myProfileId,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used inside ChatProvider');
  return ctx;
}
